import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { DeskTable, StackedDateTime, muted, type Column } from '@/components/common/DeskTable'
import FacetFilter from '@/components/common/FacetFilter'
import FilterBarApply from '@/components/common/FilterBarApply'
import FilterBarForm from '@/components/common/FilterBarForm'
import FilterSearch from '@/components/common/FilterSearch'
import PageFrame from '@/components/common/PageFrame'
import { Button } from '@/components/ui/button'
import { useDraftApplied } from '@/hooks/useDraftApplied'
import {
  deskFacetsActive,
  useAgreementBook,
  useAgreementFilterOptions,
  useAgreementTotal,
} from '@/hooks/useAgreements'
import { humanize, optionLabels, statusLabel } from '@/lib/labels'
import { emptyDeskFacets, filtersFromSearch, labels, writeFacet, writeSort } from '@/lib/desk-url'
import { useDeskSort } from '@/hooks/useDeskSort'
import { useListPageSize } from '@/hooks/useListPageSize'
import type { AgreementRow } from '@/lib/api-client'
import StatusBadge from '@/components/common/StatusBadge'
import { AGREEMENT_WIDTH, agreementColumnsForWidth } from '@/lib/column-widths'
import { dash, downloadCsv, fmtTime, splitIb } from '@/lib/format'
import { resolveOffsetPagination } from '@/lib/list-pagination'
import { isFilterChangeFetching } from '@/lib/query-client'
import { FILTER_BAR } from '@/lib/ui-shell'

function agreementCsvRow(row: AgreementRow): string[] {
  const ib = splitIb(row.ib_team)
  return [
    dash(row.user_id),
    row.full_name,
    ib.ib,
    ib.team,
    row.email,
    dash(row.demo_login),
    dash(row.live_login),
    dash(row.kyc_tier ? humanize(row.kyc_tier) : ''),
    dash(row.identity_number),
    dash(row.approved_by),
    row.status ? statusLabel(row.status) : '',
    dash(row.risk_band ? humanize(row.risk_band) : ''),
    fmtTime(row.first_deposit_at),
    fmtTime(row.created_at),
    dash(row.mobile_phone),
    dash(row.utm_source),
    dash(row.utm_medium),
    dash(row.utm_campaign),
  ]
}

const COLS = [
  'User',
  'Name',
  'IB',
  'Team',
  'Email',
  'Demo',
  'Live',
  'KYC',
  'Identity',
  'Approved by',
  'Status',
  'Risk',
  'First deposit',
  'Created',
  'WhatsApp',
  'UTM source',
  'UTM medium',
  'UTM campaign',
]

const md = (v: unknown) => muted(dash(v))
const mono = (v: unknown) => <span className="font-mono">{md(v)}</span>
const clipText = (value: unknown, className = '') => {
  const text = value == null || value === '' ? '' : String(value)
  return <span className={`block truncate ${className}`} title={text || undefined}>{md(value)}</span>
}

const AGREEMENT_SORT = ['user', 'name', 'email', 'kyc', 'identity', 'status', 'risk', 'created', 'wa', 'utms', 'utmm', 'utmc']
const aw = AGREEMENT_WIDTH
const clip = 'overflow-hidden'
const stacked = 'overflow-hidden whitespace-normal'
const agreementColumns: Column<AgreementRow>[] = [
  { key: 'user', sortKey: 'user', header: 'User', width: aw.user, className: clip, cell: (r) => clipText(r.user_id, 'font-mono') },
  {
    key: 'name',
    sortKey: 'name',
    header: 'Name',
    sticky: true,
    width: aw.name,
    className: clip,
    mobile: 'title',
    cell: (r) => (
      <Link className="block truncate font-medium hover:underline" title={r.full_name} to={`/opening/agreements/${r.client_id}`}>
        {r.full_name}
      </Link>
    ),
  },
  { key: 'ib', header: 'IB', width: aw.ib, className: clip, cell: (r) => clipText(splitIb(r.ib_team).ib) },
  { key: 'team', header: 'Team', width: aw.team, className: clip, cell: (r) => clipText(splitIb(r.ib_team).team) },
  { key: 'email', sortKey: 'email', header: 'Email', width: aw.email, className: clip, mobile: 'subtitle', cell: (r) => <span className="block truncate" title={r.email}>{r.email}</span> },
  { key: 'demo', header: 'Demo', width: aw.demo, cell: (r) => mono(r.demo_login) },
  { key: 'live', header: 'Live', width: aw.live, mobile: 'field', cell: (r) => mono(r.live_login) },
  { key: 'kyc', sortKey: 'kyc', header: 'KYC', width: aw.kyc, cell: (r) => muted(humanize(r.kyc_tier || '')) },
  { key: 'identity', sortKey: 'identity', header: 'Identity', width: aw.identity, className: clip, cell: (r) => clipText(r.identity_number, 'font-mono') },
  { key: 'approved', header: 'Approved by', width: aw.approved, className: clip, cell: (r) => clipText(r.approved_by) },
  { key: 'status', sortKey: 'status', header: 'Status', width: aw.status, className: 'whitespace-normal', mobile: 'status', cell: (r) => <StatusBadge className="whitespace-normal" status={r.status} /> },
  { key: 'risk', sortKey: 'risk', header: 'Risk', width: aw.risk, mobile: 'field', cell: (r) => muted(humanize(r.risk_band || '')) },
  { key: 'fd', header: 'First deposit', width: aw.fd, className: stacked, cell: (r) => <StackedDateTime value={r.first_deposit_at} /> },
  { key: 'created', sortKey: 'created', sortFirst: 'desc', header: 'Created', width: aw.created, className: stacked, mobile: 'field', cell: (r) => <StackedDateTime value={r.created_at} /> },
  { key: 'wa', sortKey: 'wa', header: 'WhatsApp', width: aw.wa, cell: (r) => mono(r.mobile_phone) },
  { key: 'utms', sortKey: 'utms', header: 'UTM source', width: aw.utms, className: clip, cell: (r) => clipText(r.utm_source) },
  { key: 'utmm', sortKey: 'utmm', header: 'UTM medium', width: aw.utmm, className: clip, cell: (r) => clipText(r.utm_medium) },
  { key: 'utmc', sortKey: 'utmc', header: 'UTM campaign', width: aw.utmc, className: clip, cell: (r) => clipText(r.utm_campaign) },
]
export function AgreementsPage() {
  const [sp, setSp] = useSearchParams()
  const { sort, onSortChange, sortQuery } = useDeskSort(AGREEMENT_SORT)
  const page = Number(sp.get('page') || '1')
  const pageSize = useListPageSize()
  const urlFilters = filtersFromSearch(sp)
  const { draft, applied, apply, resetTo } = useDraftApplied(urlFilters)
  const [searchDraft, setSearchDraft] = useState(urlFilters.q)
  const options = useAgreementFilterOptions(true)
  const rows = useAgreementBook({ ...applied, q: applied.q }, page, pageSize, sortQuery)
  const totalQ = useAgreementTotal({ ...applied, q: applied.q })
  const items = rows.data?.items ?? []
  const paging = resolveOffsetPagination({
    page,
    pageSize,
    total: totalQ.data,
    rowCount: items.length,
    hasMore: Boolean(rows.data?.has_more),
  })

  const spKey = sp.toString()
  const lastQ = useRef(urlFilters.q)
  useEffect(() => {
    const next = new URLSearchParams(spKey)
    resetTo(filtersFromSearch(next))
    // Facet picks commit to the URL without touching the search box; only a real q change resets the draft.
    const q = next.get('q') || ''
    if (q !== lastQ.current) {
      lastQ.current = q
      setSearchDraft(q)
    }
  }, [spKey, resetTo])

  function commit(next: typeof draft, q: string, nextPage = '1', size = String(pageSize)) {
    const cur = new URLSearchParams()
    if (q) cur.set('q', q)
    writeFacet(cur, 'status', next.status)
    writeFacet(cur, 'utm_source', next.utm_source)
    writeFacet(cur, 'utm_medium', next.utm_medium)
    writeFacet(cur, 'utm_campaign', next.utm_campaign)
    writeFacet(cur, 'kyc_tier', next.kyc_tier)
    writeFacet(cur, 'risk_band', next.risk_band)
    cur.set('page', nextPage)
    cur.set('page_size', size)
    writeSort(cur, sort)
    setSp(cur, { replace: true })
  }

  // Pickers apply at once, like Funds; only the search box waits for Apply, since it is typed.
  function facet(patch: Partial<typeof applied>) {
    const next = { ...applied, ...patch }
    resetTo(next)
    commit(next, applied.q)
  }

  const csv = useMemo(() => items.map(agreementCsvRow), [items])

  const opt = options.data
  const searchDirty = searchDraft !== applied.q

  return (
    <PageFrame
      title="Agreements"
      description="Every client application with its review status. Select a name to open the review."
      actions={
        <Button variant="outline" onClick={() => downloadCsv('agreements.csv', COLS, csv)}>
          Export CSV
        </Button>
      }
    >
      <FilterBarForm
        onSubmit={() => {
          apply()
          commit({ ...draft, q: searchDraft.trim() }, searchDraft.trim())
        }}
        className={FILTER_BAR}
      >
        <div className="flex max-lg:flex-col max-lg:gap-3 lg:flex-row lg:flex-wrap lg:items-end lg:gap-3">
          <FilterSearch value={searchDraft} onChange={setSearchDraft} placeholder="Name, email, code, identity" />
          <FacetFilter
            label="Status"
            value={draft.status}
            options={optionLabels(opt?.statuses ?? ['VERIFICATION_PENDING', 'APPROVED_BY_WP', 'ACTIVE', 'CLOSED'], statusLabel)}
            onChange={(status) => facet({ status })}
          />
          <FacetFilter
            label="UTM source"
            value={draft.utm_source}
            options={labels(opt?.utm_sources ?? [])}
            onChange={(utm_source) => facet({ utm_source })}
          />
          <FacetFilter
            label="UTM medium"
            value={draft.utm_medium}
            options={labels(opt?.utm_mediums ?? [])}
            onChange={(utm_medium) => facet({ utm_medium })}
          />
          <FacetFilter
            label="UTM campaign"
            value={draft.utm_campaign}
            options={labels(opt?.utm_campaigns ?? [])}
            onChange={(utm_campaign) => facet({ utm_campaign })}
          />
          <FacetFilter
            label="KYC"
            value={draft.kyc_tier}
            options={optionLabels(opt?.kyc_tiers ?? [], humanize)}
            onChange={(kyc_tier) => facet({ kyc_tier })}
          />
          <FacetFilter
            label="Risk"
            value={draft.risk_band}
            options={optionLabels(opt?.risk_bands ?? [], humanize)}
            onChange={(risk_band) => facet({ risk_band })}
          />
          <FilterBarApply
            isDirty={searchDirty}
            canReset={deskFacetsActive(draft) || deskFacetsActive(applied) || Boolean(searchDraft)}
            onReset={() => {
              setSearchDraft('')
              resetTo(emptyDeskFacets())
              commit(emptyDeskFacets(), '')
            }}
          />
        </div>
      </FilterBarForm>

      <DeskTable
        columns={agreementColumns}
        rows={items}
        rowKey={(r) => r.client_id}
        loading={rows.isLoading}
        error={rows.error}
        dimmed={isFilterChangeFetching(rows.isFetching, rows.isPlaceholderData)}
        emptyTitle="No agreements"
        emptyDescription="Nothing matches these filters."
        sort={sort}
        onSortChange={onSortChange}
        visibleKeys={agreementColumnsForWidth}
        paging={{
          total: paging.totalItems,
          page: paging.safePage,
          pageSize,
          onPage: (p) => commit(applied, applied.q, String(p)),
          onPageSize: (size) => commit(applied, applied.q, '1', String(size)),
        }}
      />
    </PageFrame>
  )
}
